import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import "./boxArenaCourtlineTheme.css";
import OpenMapsLink from "./OpenMapsLink";
import logoImage from "./boxarena-courtline-assets/boxarena-logo.png";
import heroPlayerImage from "./boxarena-courtline-assets/man-badminton-amber.png";
import aboutPlayerImage from "./boxarena-courtline-assets/football-shoot-amber.png";
import {
  resolveThemeArray,
  resolveThemeCopy,
  resolveThemeLogo,
  resolveThemeMedia,
  resolveThemeObject,
} from "../publicWebsiteContent";
import { resolveThemePaletteStyle } from "../themePalette";
import { isWebsiteSectionVisible } from "../themeDefinitions";

const THEME_ID = "boxarena-courtline";

const STATIC_STATS = Object.freeze([
  { value: 3, label: "Courts" },
  { value: 18, label: "Hrs open daily" },
  { value: 4, label: "Sports" },
  { value: 7, label: "Days a week" },
]);

const STATIC_SPORTS = Object.freeze([
  { name: "Badminton", detail: "2 courts, BWF-spec lines" },
  {
    name: "Futsal",
    detail: "1 turf court, rebound boards, flood lights",
  },
  { name: "Table tennis", detail: "2 tables, open lounge play" },
]);

const STATIC_GALLERY = Object.freeze([
  {
    title: "Futsal Under the Lights",
    sport: "Futsal",
    image: "https://loremflickr.com/800/600/futsal",
  },
  {
    title: "Badminton Smash",
    sport: "Badminton",
    image: "https://loremflickr.com/800/600/badminton",
  },
  {
    title: "Table Tennis Rally",
    sport: "Table Tennis",
    image: "https://loremflickr.com/800/600/table-tennis",
  },
  {
    title: "Futsal Match Action",
    sport: "Futsal",
    image: "https://loremflickr.com/800/600/futsal,indoor",
  },
  {
    title: "Indoor Badminton Court",
    sport: "Badminton",
    image: "https://loremflickr.com/800/600/badminton,court",
  },
  {
    title: "Ping Pong Close-up",
    sport: "Table Tennis",
    image: "https://loremflickr.com/800/600/pingpong",
  },
]);

const STATIC_FAQS = Object.freeze([
  {
    q: "Do I need to be a member to book a court?",
    a: "No. BOXARENA is walk-in and pay-per-slot. Members get priority booking on weekend evenings, but anyone can book a court online or at the front desk.",
  },
  {
    q: "Can I rent equipment on site?",
    a: "Yes. Rackets, futsal boots, cricket kits and bibs are all available to rent at the front desk for a flat fee per session.",
  },
  {
    q: "How far in advance should I book?",
    a: "Weekday mornings and afternoons are usually open same-day. Weeknight and weekend slots between 6pm and 10pm fill up 3–5 days ahead, so book those early.",
  },
  {
    q: "Is coaching available?",
    a: "Badminton and futsal coaching sessions run on weekday mornings for individuals and small groups. Ask at the front desk or mention it when you book.",
  },
  {
    q: "What's your cancellation policy?",
    a: "Cancel or reschedule up to 4 hours before your slot for a full credit. Cancellations inside that window forfeit the booking.",
  },
]);

const STATIC_HOURS = Object.freeze([
  { day: "Monday – Friday", time: "6:00 AM – 12:00 AM" },
  { day: "Saturday", time: "6:00 AM – 1:00 AM" },
  { day: "Sunday", time: "7:00 AM – 11:00 PM" },
]);

const STATIC_MAP_EMBED_URL =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d220.75100378201415!2d85.33256014758587!3d27.71679039488908!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39eb199206504cd1%3A0x32568b6a5f314aff!2sBoxArena-%20Pickleball%2C%20Futsal%20%26%20Badminton!5e0!3m2!1sen!2snl!4v1783320438759!5m2!1sen!2snl";

const cleanText = (value) => String(value == null ? "" : value).trim();

const formatLabel = (value) =>
  cleanText(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const uniqueText = (values) =>
  Array.from(new Set(values.map(cleanText).filter(Boolean)));

const getVenuePhone = (venue) =>
  cleanText(venue?.contact?.e164) ||
  cleanText(venue?.contact?.nationalNumber);

const getVenueAddress = (venue) => {
  const address = cleanText(venue?.location?.address);
  if (address) return address;

  return [
    cleanText(venue?.location?.city),
    cleanText(venue?.location?.country || venue?.country),
  ]
    .filter(Boolean)
    .join(", ");
};

const getVenueLocality = (venue) =>
  cleanText(venue?.location?.city) ||
  cleanText(venue?.location?.address).split(",")[0] ||
  "Kathmandu";

const formatTime = (value, fallback) => {
  const input = cleanText(value);

  if (!/^\d{1,2}:\d{2}$/.test(input)) {
    return input || fallback;
  }

  const [hourText, minute] = input.split(":");
  const hour = Number(hourText);

  if (!Number.isFinite(hour)) {
    return fallback;
  }

  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;

  return `${displayHour}:${minute} ${period}`;
};

const getOpenHoursDuration = (openTime, closeTime) => {
  const parseMinutes = (value) => {
    const match = cleanText(value).match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return null;

    const hour = Number(match[1]);
    const minute = Number(match[2]);

    if (
      !Number.isInteger(hour) ||
      !Number.isInteger(minute) ||
      hour < 0 ||
      hour > 23 ||
      minute < 0 ||
      minute > 59
    ) {
      return null;
    }

    return hour * 60 + minute;
  };

  const start = parseMinutes(openTime);
  const end = parseMinutes(closeTime);

  if (start == null || end == null) return null;

  const duration =
    end > start
      ? end - start
      : end === start
        ? 24 * 60
        : end + 24 * 60 - start;

  return Math.round(duration / 60);
};

const getGroundImage = (ground) =>
  cleanText(ground?.mainImageUrl || ground?.imageUrl);

const getGroundSport = (ground) =>
  formatLabel(
    ground?.sportName ||
      ground?.sport?.name ||
      ground?.type ||
      ground?.name,
  );

const getGroundFeatures = (ground) => {
  const source = [ground?.features, ground?.facilities, ground?.amenities]
    .find((value) => Array.isArray(value) && value.length > 0);

  return Array.isArray(source)
    ? source
        .map((item) =>
          formatLabel(
            typeof item === "string"
              ? item
              : item?.label || item?.name || item?.type,
          ),
        )
        .filter(Boolean)
    : [];
};

const getGroundDetail = (ground, index) => {
  const description = cleanText(ground?.description);
  if (description) return description;

  const features = getGroundFeatures(ground);
  if (features.length > 0) return features.slice(0, 3).join(", ");

  const capacity = Number(ground?.capacity);
  if (Number.isFinite(capacity) && capacity > 0) {
    return `${capacity} player capacity`;
  }

  return STATIC_SPORTS[index % STATIC_SPORTS.length].detail;
};

const getSafeExternalUrl = (value, fallback = "") => {
  const input = cleanText(value);
  if (!input) return fallback;

  try {
    const parsed = new URL(input);
    return ["http:", "https:"].includes(parsed.protocol)
      ? parsed.toString()
      : fallback;
  } catch {
    return fallback;
  }
};

const buildMapEmbedUrl = (venue, configuredUrl) => {
  const rawLatitude = venue?.location?.latitude;
  const rawLongitude = venue?.location?.longitude;
  const latitude = rawLatitude === null || rawLatitude === undefined || rawLatitude === ""
    ? Number.NaN
    : Number(rawLatitude);
  const longitude = rawLongitude === null || rawLongitude === undefined || rawLongitude === ""
    ? Number.NaN
    : Number(rawLongitude);
  const hasValidCoordinates =
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180;

  if (hasValidCoordinates) {
    return `https://maps.google.com/maps?q=${encodeURIComponent(`${latitude},${longitude}`)}&z=15&output=embed`;
  }

  const safeConfiguredUrl = getSafeExternalUrl(configuredUrl);
  if (safeConfiguredUrl) return safeConfiguredUrl;

  const address = getVenueAddress(venue);
  return address
    ? `https://maps.google.com/maps?q=${encodeURIComponent(address)}&z=15&output=embed`
    : STATIC_MAP_EMBED_URL;
};

const padStat = (value) =>
  String(Math.max(0, Number(value) || 0)).padStart(2, "0");

function CourtLines({ variant = "full", className = "" }) {
  if (variant === "divider") {
    return (
      <svg
        viewBox="0 0 1200 60"
        className={`bac-court-lines bac-court-lines--divider ${className}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <line
          x1="0"
          y1="30"
          x2="1200"
          y2="30"
          stroke="#35624A"
          strokeWidth="2"
          className="bac-court-line"
        />
        <circle
          cx="600"
          cy="30"
          r="18"
          stroke="#E7A339"
          strokeWidth="2"
          fill="none"
          className="bac-court-line bac-court-line--delay"
        />
      </svg>
    );
  }

  return (
    <svg
      viewBox="0 0 800 500"
      className={`bac-court-lines ${className}`}
      aria-hidden="true"
    >
      <rect x="20" y="20" width="760" height="460" fill="none" stroke="#35624A" strokeWidth="3" className="bac-court-line" />
      <line x1="20" y1="250" x2="780" y2="250" stroke="#35624A" strokeWidth="3" className="bac-court-line bac-court-line--delay" />
      <line x1="400" y1="20" x2="400" y2="480" stroke="#35624A" strokeWidth="2" strokeDasharray="6 10" />
      <rect x="90" y="90" width="620" height="320" fill="none" stroke="#C7BFA8" strokeWidth="2" className="bac-court-line bac-court-line--delay" />
      <circle cx="400" cy="250" r="60" fill="none" stroke="#E7A339" strokeWidth="2" className="bac-court-line" />
    </svg>
  );
}

function GalleryModal({ items, index, onClose, onNavigate }) {
  const item = items[index];
  if (!item) return null;

  return (
    <div className="bac-gallery-modal" onClick={onClose} role="dialog" aria-modal="true">
      <div className="bac-gallery-modal__content" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="bac-gallery-modal__close" onClick={onClose} aria-label="Close gallery">
          <X size={20} />
        </button>
        <button
          type="button"
          className="bac-gallery-modal__nav bac-gallery-modal__nav--prev"
          onClick={() => onNavigate((index - 1 + items.length) % items.length)}
          aria-label="Previous image"
        >
          <ChevronLeft size={25} />
        </button>
        <button
          type="button"
          className="bac-gallery-modal__nav bac-gallery-modal__nav--next"
          onClick={() => onNavigate((index + 1) % items.length)}
          aria-label="Next image"
        >
          <ChevronRight size={25} />
        </button>
        <img src={item.image} alt={item.title} className="bac-gallery-modal__image" />
        <div className="bac-gallery-modal__caption">
          <strong>{item.title}</strong>
          <span>{index + 1} / {items.length}</span>
        </div>
      </div>
    </div>
  );
}

function Gallery({ items, eyebrow, title, previewMode }) {
  const viewportRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [modalIndex, setModalIndex] = useState(null);

  const scrollToIndex = (index) => {
    const viewport = viewportRef.current;
    if (!viewport || !viewport.children.length) return;

    const firstCard = viewport.children[0];
    const cardWidth = firstCard.getBoundingClientRect().width + 24;
    const nextIndex = (index + items.length) % items.length;

    viewport.scrollTo({
      left: nextIndex * cardWidth,
      behavior: "smooth",
    });
    setActiveIndex(nextIndex);
  };

  const move = (direction) => scrollToIndex(activeIndex + direction);

  useEffect(() => {
    if (previewMode || items.length <= 1 || modalIndex !== null) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setActiveIndex((currentIndex) => {
        const nextIndex = (currentIndex + 1) % items.length;
        const viewport = viewportRef.current;

        if (viewport?.children?.length) {
          const firstCard = viewport.children[0];
          const cardWidth = firstCard.getBoundingClientRect().width + 24;
          viewport.scrollTo({
            left: nextIndex * cardWidth,
            behavior: "smooth",
          });
        }

        return nextIndex;
      });
    }, 4000);

    return () => window.clearInterval(timer);
  }, [items.length, modalIndex, previewMode]);

  const handleScroll = () => {
    const viewport = viewportRef.current;
    if (!viewport || !viewport.children.length) return;

    const firstCard = viewport.children[0];
    const cardWidth = firstCard.getBoundingClientRect().width + 24;
    const index = Math.round(viewport.scrollLeft / Math.max(cardWidth, 1));
    setActiveIndex(Math.min(items.length - 1, Math.max(0, index)));
  };

  return (
    <section className="bac-gallery" aria-label={title}>
      <p className="bac-eyebrow">{eyebrow}</p>
      <h2 className="bac-section-title">{title}</h2>

      <div
        className="bac-gallery__track"
        ref={viewportRef}
        onScroll={handleScroll}
      >
        {items.map((item, index) => (
          <button
            type="button"
            className="bac-gallery-card"
            key={`${item.image}-${index}`}
            onClick={() => !previewMode && setModalIndex(index)}
          >
            <img src={item.image} alt={item.title} loading="lazy" />
            <span className="bac-gallery-card__overlay" />
            <span className="bac-gallery-card__sport">{item.sport}</span>
            <strong>{item.title}</strong>
          </button>
        ))}
      </div>

      <div className="bac-gallery__pagination" aria-hidden="true">
        {items.map((item, index) => (
          <span
            key={`${item.image}-dot-${index}`}
            className={index === activeIndex ? "active" : ""}
          />
        ))}
      </div>

      <div className="bac-gallery__controls">
        <button type="button" onClick={() => move(-1)} aria-label="Previous gallery items">
          <ChevronLeft size={18} />
        </button>
        <button type="button" onClick={() => move(1)} aria-label="Next gallery items">
          <ChevronRight size={18} />
        </button>
      </div>

      {modalIndex !== null && !previewMode && (
        <GalleryModal
          items={items}
          index={modalIndex}
          onClose={() => setModalIndex(null)}
          onNavigate={setModalIndex}
        />
      )}
    </section>
  );
}

function FaqItem({ item, isOpen, onToggle }) {
  return (
    <div className="bac-faq-item">
      <button type="button" onClick={onToggle} aria-expanded={isOpen}>
        <span>{item.q}</span>
        <span className={isOpen ? "open" : ""} aria-hidden="true">+</span>
      </button>
      <div className={isOpen ? "bac-faq-answer open" : "bac-faq-answer"}>
        <div><p>{item.a}</p></div>
      </div>
    </div>
  );
}

export default function BoxArenaCourtlineTheme({
  venue = {},
  grounds = [],
  website = {},
  primaryColor = "#E7A339",
  accentColor = "#101913",
  openBooking = () => {},
  previewMode = false,
}) {
  const [scrolled, setScrolled] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  const themePaletteStyle = resolveThemePaletteStyle(
    THEME_ID,
    primaryColor,
    accentColor,
  );

  const t = (key, fallback, apiValue = "") =>
    resolveThemeCopy(website, THEME_ID, key, fallback, apiValue);

  const m = (key, fallback) =>
    resolveThemeMedia(website, THEME_ID, key, fallback);

  const social = resolveThemeObject(website, THEME_ID, "social", {});

  useEffect(() => {
    if (previewMode) return undefined;

    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [previewMode]);

  const venueGrounds = Array.isArray(grounds) ? grounds : [];
  const venueName = cleanText(venue?.name) || "BOXARENA";
  const usesBoxArenaBrand =
    cleanText(venueName)
      .replace(/[\s_-]+/g, "")
      .toLowerCase()
      .includes("boxarena");
  const apiVenueAddress = getVenueAddress(venue);
  const venueAddress = apiVenueAddress || "Kathmandu, Nepal";
  const venueLocality = getVenueLocality(venue);
  const venueCountry = cleanText(venue?.location?.country || venue?.country) || "Nepal";
  const venuePhone = getVenuePhone(venue) || "+91 11 4123 4519";

  const rawOpenTime = cleanText(venue?.policies?.openTime);
  const rawCloseTime = cleanText(venue?.policies?.closeTime);
  const apiHoursAvailable = Boolean(rawOpenTime && rawCloseTime);
  const openingTime = formatTime(rawOpenTime, "6:00 AM");
  const closingTime = formatTime(rawCloseTime, "12:00 AM");
  const displayHours = `${openingTime} – ${closingTime}`;
  const openHoursDuration =
    getOpenHoursDuration(rawOpenTime, rawCloseTime) || STATIC_STATS[1].value;

  const sportLabels = uniqueText(
    venueGrounds.map((ground) => getGroundSport(ground)),
  );

  const sports = venueGrounds.length > 0
    ? venueGrounds.slice(0, 6).map((ground, index) => ({
        name: cleanText(ground?.name) || getGroundSport(ground) || `Court ${index + 1}`,
        detail: getGroundDetail(ground, index),
        ground,
      }))
    : STATIC_SPORTS.map((sport) => ({ ...sport, ground: null }));

  const venueGallery = Array.isArray(venue?.media?.gallery)
    ? venue.media.gallery.filter(Boolean)
    : [];

  const apiGallery = venueGrounds
    .map((ground, index) => {
      const image = getGroundImage(ground) || venueGallery[index];
      if (!image) return null;

      return {
        image,
        title: cleanText(ground?.name) || `Court ${index + 1}`,
        sport: getGroundSport(ground) || "Sports",
      };
    })
    .filter(Boolean);

  venueGallery.forEach((image, index) => {
    if (!apiGallery.some((item) => item.image === image)) {
      apiGallery.push({
        image,
        title: `${venueName} Gallery ${index + 1}`,
        sport: sportLabels[index % Math.max(sportLabels.length, 1)] || "Venue",
      });
    }
  });

  const galleryItems = apiGallery.length > 0 ? apiGallery : STATIC_GALLERY;

  const stats = [
    {
      value: venueGrounds.length || STATIC_STATS[0].value,
      label: t("statCourtsLabel", "Courts"),
    },
    {
      value: openHoursDuration,
      label: t("statHoursLabel", "Hrs open daily"),
    },
    {
      value: sportLabels.length || STATIC_STATS[2].value,
      label: t("statSportsLabel", "Sports"),
    },
    {
      value: STATIC_STATS[3].value,
      label: t("statDaysLabel", "Days a week"),
    },
  ];

  const apiFaqs = STATIC_FAQS.map((faq, index) => {
    if (
      index === 1 &&
      Array.isArray(venue?.facilities) &&
      venue.facilities.length > 0
    ) {
      return {
        ...faq,
        a: `Available venue facilities include ${venue.facilities
          .map(formatLabel)
          .join(", ")}. Contact the venue to confirm rental equipment and current charges.`,
      };
    }

    if (index === 4 && cleanText(venue?.policies?.cancellationPolicy)) {
      return { ...faq, a: cleanText(venue.policies.cancellationPolicy) };
    }

    return faq;
  });

  const faqs = resolveThemeArray(
    website,
    THEME_ID,
    "faqs",
    STATIC_FAQS,
    apiFaqs,
  ).map((faq, index) => {
    const fallback = STATIC_FAQS[index % STATIC_FAQS.length];
    return {
      q: cleanText(faq?.q || faq?.question) || fallback.q,
      a: cleanText(faq?.a || faq?.answer) || fallback.a,
    };
  });

  const hoursRows = apiHoursAvailable
    ? STATIC_HOURS.map((row) => ({ ...row, time: displayHours }))
    : STATIC_HOURS;

  const configuredEmail = cleanText(social?.contactEmail);
  const contactEmail =
    configuredEmail ||
    cleanText(venue?.contact?.email || venue?.email) ||
    "play@boxarena.onground.com";
  const mapEmbedUrl = buildMapEmbedUrl(venue, social?.mapEmbedUrl);
  const apiFooterVenueLine =
    apiVenueAddress || apiHoursAvailable
      ? `${venueAddress} — Open ${openingTime} to ${closingTime}, daily`
      : "";

  const resolvedLogo = resolveThemeLogo(
    website,
    THEME_ID,
    usesBoxArenaBrand ? logoImage : "",
  );
  const resolvedHeroPlayer = m("heroPlayerImageUrl", heroPlayerImage);
  const resolvedAboutPlayer = m("aboutPlayerImageUrl", aboutPlayerImage);

  const navLinks = [
    { id: "about", sectionKey: "about", label: t("navAbout", "About") },
    { id: "faq", sectionKey: "faqs", label: t("navFaq", "FAQ") },
    { id: "location", sectionKey: "location", label: t("navLocation", "Location") },
  ].filter(({ sectionKey }) =>
    isWebsiteSectionVisible(website, sectionKey),
  );

  const scrollTo = (id) => {
    if (previewMode) return;
    const target = document.getElementById(id);

    if (target) {
      target.scrollIntoView({ behavior: "smooth" });
      return;
    }

    if (id === "top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleBooking = (ground = null) => {
    if (!previewMode) openBooking(ground);
  };

  const heroDescription = t(
    "heroDescription",
    "Badminton, futsal and box cricket on regulation-marked courts, open every day from 6am to midnight. Walk in, warm up, play.",
    cleanText(venue?.shortDescription),
  );

  const aboutDescription = t(
    "aboutParagraphOne",
    "BOXARENA opened in 2019 as a single badminton hall and grew into a four-sport arena by popular demand. There is no membership desk to get past and no minimum booking — just courts, kept in match condition, ready when you are.",
    cleanText(venue?.fullDescription),
  );

  return (
    <div
      className={`bac-theme ${previewMode ? "bac-theme-preview" : ""}`}
      style={themePaletteStyle}
    >
      <header className={`bac-nav ${scrolled ? "scrolled" : ""}`}>
        <nav>
          <button type="button" className="bac-logo" onClick={() => scrollTo("top")}>
            {resolvedLogo ? (
              <img src={resolvedLogo} alt={`${venueName} logo`} />
            ) : (
              <span
                aria-label={`${venueName} logo`}
                style={{
                  fontWeight: 900,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                {venueName}
              </span>
            )}
          </button>

          <ul>
            {navLinks.map((link) => (
              <li key={link.id}>
                <button type="button" onClick={() => scrollTo(link.id)}>{link.label}</button>
              </li>
            ))}
          </ul>

          <button type="button" className="bac-nav-book" onClick={() => handleBooking()}>
            {t("navBookLabel", "Book a Court")}
          </button>
        </nav>
      </header>

      <main>
        {isWebsiteSectionVisible(website, "hero") && (
        <section id="top" className="bac-hero">
          <div className="bac-hero__court-lines"><CourtLines /></div>
          <img className="bac-hero__player" src={resolvedHeroPlayer} alt="" />

          <div className="bac-container bac-hero__content">
            <p className="bac-eyebrow">
              {t("heroEyebrow", "Kathmandu, Nepal", `${venueLocality}, ${venueCountry}`)}
            </p>

            <h1>
              {t("heroTitleLineOne", "Book the court.")}
              <br />
              {t("heroTitleLineTwo", "Own the game.")}
            </h1>

            <p className="bac-hero__description">{heroDescription}</p>

            <div className="bac-hero__actions">
              <button type="button" className="bac-button bac-button--primary" onClick={() => handleBooking()}>
                {t("heroPrimaryLabel", "Check availability")}
              </button>
              {isWebsiteSectionVisible(website, "about") && (
                <button type="button" className="bac-button bac-button--outline" onClick={() => scrollTo("about")}>
                  {t("heroSecondaryLabel", "See the venue")}
                </button>
              )}
            </div>

            <dl className="bac-stats">
              {stats.map((stat) => (
                <div key={stat.label}>
                  <dt>{stat.label}</dt>
                  <dd>{padStat(stat.value)}</dd>
                  <dd>{stat.label}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
        )}

        {isWebsiteSectionVisible(website, "about") && (
        <>
        <section id="about" className="bac-about">
          <img className="bac-about__player" src={resolvedAboutPlayer} alt="" />
          <div className="bac-container bac-about__grid">
            <div>
              <p className="bac-eyebrow">{t("aboutEyebrow", "About the venue")}</p>
              <h2 className="bac-section-title">
                {t("aboutTitle", "Built for players, not spectators")}
              </h2>
              <p>{aboutDescription}</p>
              <p>
                {t(
                  "aboutParagraphTwo",
                  "Coaching sessions run in the mornings, corporate leagues take over on weeknights, and the courts stay open to walk-ins the rest of the time. Shoes, rackets and bibs are available to rent at the front desk.",
                )}
              </p>

              <ul className="bac-sports-list">
                {sports.map((sport, index) => (
                  <li key={`${sport.name}-${index}`}>
                    <button
                      type="button"
                      onClick={() => handleBooking(sport.ground)}
                      aria-label={`Book ${sport.name}`}
                    >
                      <span>{sport.name}</span>
                      <small>{sport.detail}</small>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bac-gallery-desktop">
              <Gallery
                items={galleryItems}
                eyebrow={t("galleryEyebrow", "Take a look around")}
                title={t("galleryTitle", "Our Gallery")}
                previewMode={previewMode}
              />
            </div>
          </div>
        </section>

        <div className="bac-gallery-mobile bac-container">
          <Gallery
            items={galleryItems}
            eyebrow={t("galleryEyebrow", "Take a look around")}
            title={t("galleryTitle", "Our Gallery")}
            previewMode={previewMode}
          />
        </div>
        </>
        )}

        {isWebsiteSectionVisible(website, "faqs") && (
        <section id="faq" className="bac-faq">
          <div className="bac-container">
            <CourtLines variant="divider" className="bac-faq__divider" />
            <p className="bac-eyebrow">{t("faqEyebrow", "Before you book")}</p>
            <h2 className="bac-section-title">{t("faqTitle", "Frequently asked")}</h2>

            <div className="bac-faq-list">
              {faqs.map((faq, index) => (
                <FaqItem
                  key={`${faq.q}-${index}`}
                  item={faq}
                  isOpen={openFaqIndex === index}
                  onToggle={() => setOpenFaqIndex(openFaqIndex === index ? -1 : index)}
                />
              ))}
            </div>
          </div>
        </section>
        )}

        {isWebsiteSectionVisible(website, "location") && (
        <section id="location" className="bac-location">
          <div className="bac-location__court-lines"><CourtLines /></div>
          <div className="bac-container">
            <p className="bac-eyebrow">{t("locationEyebrow", "Find us")}</p>
            <h2 className="bac-section-title">{t("locationTitle", "On the ground")}</h2>

            <div className="bac-location__grid">
              <div className="bac-location__details">
                <div>
                  <h3>{t("addressLabel", "Address")}</h3>
                  <p>{venueAddress}</p>
                </div>

                <div>
                  <h3>{t("hoursLabel", "Hours")}</h3>
                  <table>
                    <tbody>
                      {hoursRows.map((row) => (
                        <tr key={row.day}>
                          <td>{row.day}</td>
                          <td>{row.time}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="bac-location__contacts">
                  <div>
                    <h3>{t("phoneLabel", "Phone")}</h3>
                    <a href={`tel:${venuePhone.replace(/\s+/g, "")}`}>{venuePhone}</a>
                  </div>
                  <div>
                    <h3>{t("emailLabel", "Email")}</h3>
                    <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
                  </div>
                </div>
              </div>

              <div className="bac-map" style={{ position: 'relative' }}>
                <iframe
                  src={mapEmbedUrl}
                  title={`${venueName} location`}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <span />
                <OpenMapsLink
                  url={venue?.googleMapsUrl}
                  latitude={venue?.location?.latitude}
                  longitude={venue?.location?.longitude}
                  venueName={venueName}
                  label={t("openMapsLabel", "Open in Maps")}
                />
              </div>
            </div>
          </div>
        </section>
        )}
      </main>

      <footer className="bac-footer">
        <div className="bac-container">
          {resolvedLogo ? (
            <img src={resolvedLogo} alt={`${venueName} logo`} />
          ) : (
            <span
              aria-label={`${venueName} logo`}
              style={{
                fontWeight: 900,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {venueName}
            </span>
          )}
          <p>
            {t(
              "footerVenueLine",
              "Kathmandu, Nepal — Open 6am to midnight, daily",
              apiFooterVenueLine,
            )}
          </p>
          <p>© {new Date().getFullYear()} {venueName}{t("footerCopyrightSuffix", ".")}</p>
        </div>
      </footer>
    </div>
  );
}
